import { useEffect, useMemo, useRef, useState } from "react";
import type { ChangeEvent, FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import { acquisitionDetailLabels, acquisitionLabels } from "../acquisition";
import { formatAverage, ratingStats } from "../card-ratings";
import type { OwnerRating } from "../card-ratings";
import { readChosenPhoto } from "../chosen-photo";
import { DateField } from "../date-field";
import { threadKey } from "../forum-threads";
import type { OwnerThread } from "../forum-threads";
import { extractPdfScans, jpegSize } from "../pdf-scans";
import { readTakenAt } from "../photo-time";
import { formatDate } from "../postcard-dates";
import { countryFromParam, stateFromParam, titleCase } from "../places";
import { OnlyYou } from "../rating-controls";
import { sanitizeImage } from "../sanitize-image";
import { senderKey } from "../sender-runs";
import {
  guessRoles, itemsFromRoles, mailingPhotos, mailingTally, matchesSentCard, pairPhotos, parseDocTitle, rolesFromItems,
  sentDateFrom, sentToPlace, sha256Hex, skipRepeatedScans,
} from "../sent-uploads";
import type { LayoutItem, MailingKind, PageRole, PickedPhoto } from "../sent-uploads";

/** One photo of a mailing as the owner's /api/sent returns it. */
type SentPhoto = {
  id: string;
  url: string;
  side: "front" | "back" | "";
  /** The page's number in the doc it came from, from 0. */
  position: number;
  uploaded: boolean;
  hash: string;
};

/** A mailing (#63) as the owner's /api/sent returns it: its fields, then its items in order. */
type Mailing = {
  id: string;
  recipientUser: string;
  recipientName: string;
  country: string;
  region: string;
  sentDate: string;
  acquisitionType: string;
  acquisitionDetail: string;
  thread: string;
  group: string;
  topic: string;
  note: string;
  sourceName: string;
  isDraft: boolean;
  createdAt: string;
  /** What the tile shows: the picture she picked, or the default when coverPicked is false. */
  coverPhotoId: string;
  coverPicked: boolean;
  items: { id: string; kind: MailingKind; photos: SentPhoto[] }[];
  /** Pages whose photos haven't arrived yet. */
  pending: SentPhoto[];
};

/** The fields of a received card this page reads, from the owner's /api/postcards. */
type ReceivedCard = {
  id: string;
  senderUser: string;
  originCountry: string;
  receivedDate: string;
  createdAt: string;
  ownerRating?: OwnerRating;
  ownerThread?: OwnerThread;
};

type Viewer = "loading" | "owner" | "other";

/** A photo picked for the upload, copied off the phone the moment it was picked. */
type Photo = PickedPhoto & { file: File; url: string };

type Fields = Pick<Mailing, "recipientUser" | "recipientName" | "country" | "region" | "sentDate"
  | "acquisitionType" | "acquisitionDetail" | "thread" | "group" | "topic" | "note">;

const FIELD_NAMES: (keyof Fields)[] = [
  "recipientUser", "recipientName", "country", "region", "sentDate",
  "acquisitionType", "acquisitionDetail", "thread", "group", "topic", "note",
];

function fieldsOf(mailing: Mailing): Fields {
  return Object.fromEntries(FIELD_NAMES.map((name) => [name, mailing[name]])) as Fields;
}

const isPdf = (file: File) => file.type === "application/pdf" || /\.pdf$/i.test(file.name);

function whereTo(place: Pick<Mailing, "country" | "region">) {
  return [place.region, place.country].filter(Boolean).join(", ");
}

/** "WBMP RR · Group 356 · Decorated". */
function threadLine(mailing: Pick<Mailing, "thread" | "group" | "topic">) {
  return [mailing.thread, mailing.group && `Group ${mailing.group}`, mailing.topic].filter(Boolean).join(" · ");
}

/** "@quietcove", or her name for someone who isn't on Postcrossing. */
function whoTo(mailing: Pick<Mailing, "recipientUser" | "recipientName">) {
  return mailing.recipientUser ? `@${mailing.recipientUser}` : mailing.recipientName || "Someone";
}

/** Who a mailing went to, as one key: the username, or her name for someone without one. */
function personOf(mailing: Pick<Mailing, "recipientUser" | "recipientName">) {
  return senderKey(mailing.recipientUser) || `name:${mailing.recipientName.trim().toLowerCase()}`;
}

const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;

async function errorFrom(response: Response, fallback: string) {
  const payload = await response.json().catch(() => ({})) as { error?: string };
  return new Error(payload.error ?? fallback);
}

/** Every photo of a mailing, in the order of the doc it came from. */
function pagesOf(mailing: Mailing) {
  return mailing.items.flatMap((item) => item.photos).sort((a, b) => a.position - b.position);
}

/** The items as the server stores them, with each photo as an index into the pages. */
function itemsByPage(mailing: Mailing, pages: SentPhoto[]) {
  return mailing.items.map((item) => ({
    kind: item.kind,
    pages: item.photos.map((photo) => ({ page: pages.indexOf(photo), side: photo.side })),
  }));
}

const ROLE_NAMES: [PageRole, string][] = [
  ["env-front", "Envelope front"], ["env-back", "Envelope back"], ["front", "Card front"], ["back", "Card back"],
  ["letter", "Letter page"], ["extras", "Extras"], ["skip", "Leave it out"],
];

/** The tile's picture until she picks one: the first postcard's front, else the first page kept. */
function defaultCover(items: LayoutItem[], pages: SentPhoto[]) {
  const card = items.find((item) => item.kind === "postcard" && item.pages.some((p) => p.side === "front" && pages[p.page].uploaded));
  const front = card?.pages.find((p) => p.side === "front");
  if (front) return pages[front.page].id;
  const kept = items.flatMap((item) => item.pages.map((p) => p.page)).sort((a, b) => a - b);
  return kept.map((page) => pages[page]).find((photo) => photo.uploaded)?.id ?? "";
}

/**
 * The form for one mailing: checking it on the review list, or changing one saved
 * earlier. The pages come first, each saying what the tracker took it for; she taps one
 * to change it. In the review, the boxes the upload filled in are green, so she can see
 * what came from the file name or the photo and what is still hers to fill.
 */
function MailingForm({ mailing, mode, knownThreads, knownCountries, onSaved, onSkip, onRemove, onCancel }: {
  mailing: Mailing;
  mode: "review" | "edit";
  knownThreads: string[];
  knownCountries: string[];
  onSaved: (mailing: Mailing) => void;
  onSkip?: () => void;
  onRemove: () => Promise<void>;
  onCancel?: () => void;
}) {
  const pages = useMemo(() => pagesOf(mailing), [mailing]);
  const [fields, setFields] = useState<Fields>(() => fieldsOf(mailing));
  // What the upload filled in, fixed when the mailing opens: a box she has since changed is
  // hers, and stops saying where it came from.
  const [fromUpload, setFromUpload] = useState<ReadonlySet<keyof Fields>>(() => new Set(
    mode === "review" ? FIELD_NAMES.filter((name) => mailing[name].trim()) : [],
  ));
  const [roles, setRoles] = useState<PageRole[]>(() => rolesFromItems(itemsByPage(mailing, pages), pages.length));
  const [choosing, setChoosing] = useState<number | null>(null);
  const [cover, setCover] = useState(mailing.coverPicked ? mailing.coverPhotoId : "");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  // PDFs carry no date, so a sent date filled in by the upload came from a photo.
  const photoDate = !/\.pdf$/i.test(mailing.sourceName);

  const { items, labels, problems } = useMemo(() => itemsFromRoles(roles), [roles]);
  const kept = useMemo(() => items.flatMap((item) => item.pages.map((p) => p.page)).sort((a, b) => a - b), [items]);
  const pickedCover = cover && kept.some((page) => pages[page].id === cover) ? cover : "";
  const tile = pickedCover || defaultCover(items, pages);

  function change(name: keyof Fields, value: string) {
    setFields((current) => ({ ...current, [name]: value }));
    setFromUpload((current) => {
      if (!current.has(name)) return current;
      const next = new Set(current);
      next.delete(name);
      return next;
    });
  }

  function setRole(page: number, role: PageRole) {
    setRoles((current) => current.map((old, index) => index === page ? role : old));
    setChoosing(null);
  }

  const input = (name: keyof Fields) => ({
    id: `sent-${name}`,
    value: fields[name],
    className: fromUpload.has(name) ? "is-from-upload" : undefined,
    onChange: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => change(name, event.target.value),
  });
  const source = (name: keyof Fields) => fromUpload.has(name) && (
    <span className="from-upload">{name === "sentDate" && photoDate ? "from the photo's date · check it" : "from the file name"}</span>
  );

  async function save(event: FormEvent) {
    event.preventDefault();
    const waiting = kept.find((page) => !pages[page].uploaded);
    const problem = problems.size > 0 ? `Page ${pages[[...problems][0]].position + 1} can't be that: there's only one envelope. Change it, or leave it out.`
      : kept.length === 0 ? "Keep at least one page to save this mailing."
        : waiting !== undefined ? `Page ${pages[waiting].position + 1} hasn't finished uploading. Choose the file again to finish it, or leave that page out.`
          : !fields.recipientUser.trim() && !fields.recipientName.trim() ? "Add the recipient's username or name to save this mailing."
            : "";
    if (problem) {
      setMessage(problem);
      if (problem.startsWith("Add the recipient")) document.getElementById("sent-recipientUser")?.focus();
      return;
    }
    setBusy(true);
    setMessage("");
    try {
      const response = await fetch(`/api/sent/mailings/${mailing.id}`, {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          ...fields,
          items: items.map((item) => ({ kind: item.kind, photos: item.pages.map((p) => ({ id: pages[p.page].id, side: p.side })) })),
          coverPhotoId: pickedCover,
        }),
      });
      if (!response.ok) throw await errorFrom(response, "Could not save that mailing.");
      const payload = await response.json() as { mailing: Mailing };
      onSaved(payload.mailing);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Could not save that mailing.");
      setBusy(false);
    }
  }

  async function remove() {
    setBusy(true);
    setMessage("");
    try {
      await onRemove();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Could not remove that mailing.");
      setBusy(false);
    }
  }

  return (
    <form className="sent-form" onSubmit={save}>
      <div className="sent-layout">
        {mailing.sourceName && <p className="sent-source">From <strong>{mailing.sourceName}</strong> · {pages.length} page{pages.length === 1 ? "" : "s"}</p>}
        {mode === "review" && pages.length > 1 && <p className="ratings-hint">This is the tracker's guess at each page. Tap a page to change it.</p>}
        <ol className="sent-scans">
          {pages.map((photo, page) => (
            <li key={photo.id}>
              <button type="button" className={`sent-scan${choosing === page ? " is-choosing" : ""}${roles[page] === "skip" ? " is-left-out" : ""}`}
                aria-pressed={choosing === page} onClick={() => setChoosing(choosing === page ? null : page)}>
                {photo.uploaded
                  ? <img src={`${photo.url}?w=320`} alt="" loading="lazy" />
                  : <span className="sent-scan-missing">Not uploaded yet</span>}
                <span className="sent-scan-number">Page {photo.position + 1}</span>
                <span className={`sent-role role-${problems.has(page) ? "problem" : roles[page]}`}>{labels[page]}</span>
              </button>
            </li>
          ))}
        </ol>
        {choosing !== null && (
          <div className="sent-role-picker">
            <p>Page {pages[choosing].position + 1} is…</p>
            <div className="sent-role-choices">
              {ROLE_NAMES.map(([role, name]) => (
                <button key={role} type="button" className="sent-role-choice" aria-pressed={roles[choosing] === role} onClick={() => setRole(choosing, role)}>{name}</button>
              ))}
            </div>
            {pages[choosing].uploaded && (
              <a className="link-button" href={pages[choosing].url} target="_blank" rel="noreferrer">Open page {pages[choosing].position + 1} full size</a>
            )}
          </div>
        )}
        <div className="sent-keeps">
          <p><strong>What gets saved:</strong> {mailingTally(items)}</p>
          {kept.some((page) => pages[page].uploaded) && (
            <div className="sent-cover">
              <p>Picture for the tile {!pickedCover && <span className="muted">(the first postcard's front until you pick)</span>}</p>
              <div className="sent-cover-options">
                {kept.filter((page) => pages[page].uploaded).map((page) => (
                  <button key={pages[page].id} type="button" className="sent-cover-option" aria-pressed={tile === pages[page].id}
                    aria-label={`Use page ${pages[page].position + 1} for the tile`} onClick={() => setCover(pages[page].id)}>
                    <img src={`${pages[page].url}?w=320`} alt="" loading="lazy" />
                    <span>{tile === pages[page].id ? "On the tile" : `Page ${pages[page].position + 1}`}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="form-grid sent-fields">
        <label>
          <span className="sent-label">Recipient username{source("recipientUser")}</span>
          <input {...input("recipientUser")} placeholder="@username" autoComplete="off" maxLength={60} />
        </label>
        <label>
          <span className="sent-label"><span>Real name <OnlyYou /></span>{source("recipientName")}</span>
          <input {...input("recipientName")} autoComplete="off" maxLength={120} />
        </label>
        <p className="sent-whom full-width">A username or a name is enough. Penpals and friends don't need a username.</p>
        <label>
          <span className="sent-label">Country{source("country")}</span>
          <input {...input("country")} list="sent-known-countries" autoComplete="off" maxLength={80} />
        </label>
        <datalist id="sent-known-countries">{knownCountries.map((name) => <option key={name} value={name} />)}</datalist>
        <label>
          <span className="sent-label">State, province, or region{source("region")}</span>
          <input {...input("region")} autoComplete="off" maxLength={80} />
        </label>
        <div className={`full-width${fromUpload.has("sentDate") ? " is-from-upload" : ""}`}>
          <DateField label="Sent date or month" pickLabel="Pick the sent date" name="sentDate" value={fields.sentDate} onChange={(value) => change("sentDate", value)} />
          {source("sentDate")}
        </div>
        <label>
          <span className="sent-label">How I found them{source("acquisitionType")}</span>
          <select {...input("acquisitionType")}>
            <option value="">Choose one</option>
            {Object.entries(acquisitionLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </label>
        <label>
          <span className="sent-label">Postcrossing forum type{source("acquisitionDetail")}</span>
          <select {...input("acquisitionDetail")}>
            <option value="">Not applicable</option>
            {Object.entries(acquisitionDetailLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </label>
        <label className="full-width">
          <span className="sent-label"><span>Thread <OnlyYou /></span>{source("thread")}</span>
          <input {...input("thread")} list="sent-known-threads" autoComplete="off" maxLength={120} />
        </label>
        <datalist id="sent-known-threads">{knownThreads.map((name) => <option key={name} value={name} />)}</datalist>
        <label>
          <span className="sent-label">Group{source("group")}</span>
          <input {...input("group")} autoComplete="off" maxLength={40} />
        </label>
        <label>
          <span className="sent-label">Topic{source("topic")}</span>
          <input {...input("topic")} autoComplete="off" maxLength={80} />
        </label>
        <label className="full-width">
          <span className="sent-label"><span>Note <OnlyYou /></span></span>
          <textarea {...input("note")} rows={2} maxLength={2000} placeholder="What you wrote about, so the next one says something new" />
        </label>
      </div>

      <div className="sent-actions">
        <div className="sent-actions-main">
          <button type="submit" className="primary-button" disabled={busy}>{mode === "review" ? "Save and next" : "Save changes"}</button>
          {mode === "review"
            ? <button type="button" className="secondary-button" disabled={busy} onClick={onSkip}>Skip for now</button>
            : <button type="button" className="secondary-button" disabled={busy} onClick={onCancel}>Cancel</button>}
        </div>
        {confirmRemove ? (
          <span className="sent-confirm">
            {mode === "review" ? "Take this mailing off the list?" : "Delete this mailing? Its photos stay in the nightly backup."}
            <button type="button" className="text-button is-danger" disabled={busy} onClick={remove}>
              {mode === "review" ? "Remove it" : "Delete it"}
            </button>
            <button type="button" className="text-button" onClick={() => setConfirmRemove(false)}>Keep it</button>
          </span>
        ) : (
          <button type="button" className="text-button is-quiet" disabled={busy} onClick={() => setConfirmRemove(true)}>
            {mode === "review" ? "Remove from the list" : "Delete this mailing"}
          </button>
        )}
      </div>
      <p className="sent-message" aria-live="polite">{message}</p>
    </form>
  );
}

/** One mailing in the lookup: its tile picture, stacked when there's more than one thing in it. */
function MailingTile({ mailing, onOpen }: { mailing: Mailing; onOpen: (opener: HTMLElement) => void }) {
  const cover = pagesOf(mailing).find((photo) => photo.id === mailing.coverPhotoId && photo.uploaded);
  const things = mailing.items.length;
  return (
    <button type="button" className="sent-tile" onClick={(event) => onOpen(event.currentTarget)}>
      <span className={`sent-tile-cover${things > 2 ? " is-stack-3" : things > 1 ? " is-stack-2" : ""}`}>
        {cover ? <img src={`${cover.url}?w=640`} alt="" loading="lazy" /> : <span className="sent-tile-empty">No photo</span>}
        {things > 1 && <span className="sent-tile-badge">{things} things</span>}
      </span>
      <span className="sent-tile-meta">
        <strong>{whoTo(mailing)}</strong>
        <span className="muted">{mailing.sentDate ? `Sent ${formatDate(mailing.sentDate)}` : "No sent date"}</span>
        <span className="sent-tally">{mailingTally(mailing.items.map((item) => ({ kind: item.kind, pages: item.photos })))}</span>
      </span>
    </button>
  );
}

/** What the open mailing shows: the whole mailing, one photo full size, or the form to change it. */
type SheetView = "mailing" | "edit" | number;

/**
 * An opened mailing (#66): its details and each thing in it, a reader to page through
 * every photo full size and pick the tile picture, and the way to change or delete it.
 */
function MailingSheet({ mailing, view, onView, onClose, onChanged, onRemove, knownThreads, knownCountries }: {
  mailing: Mailing;
  view: SheetView;
  onView: (view: SheetView) => void;
  onClose: () => void;
  onChanged: (mailing: Mailing) => void;
  onRemove: () => Promise<void>;
  knownThreads: string[];
  knownCountries: string[];
}) {
  const photos = useMemo(() => mailingPhotos(mailing.items).filter(({ photo }) => photo.uploaded), [mailing]);
  const closeButton = useRef<HTMLButtonElement>(null);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const reading = typeof view === "number" ? photos[view] : undefined;
  const title = [`To ${whoTo(mailing)}`, mailing.sentDate && formatDate(mailing.sentDate)].filter(Boolean).join(" · ");

  useEffect(() => { closeButton.current?.focus(); }, []);

  function go(next: SheetView) {
    setMessage("");
    setConfirmDelete(false);
    onView(next);
  }

  // Escape closes, except on the form, where it would throw away what she typed. In the
  // reader the arrow keys go to the photo before or after. The listener is on the page,
  // not the dialog, because the Before button loses focus when it greys out on the first
  // photo. It reads the latest view through a ref so it is added once.
  const onKey = useRef<(event: KeyboardEvent) => void>(() => {});
  useEffect(() => {
    onKey.current = (event) => {
      if (view === "edit") return;
      if (event.key === "Escape") onClose();
      if (typeof view !== "number") return;
      if (event.key === "ArrowLeft" && view > 0) go(view - 1);
      if (event.key === "ArrowRight" && view < photos.length - 1) go(view + 1);
    };
  });
  useEffect(() => {
    const listener = (event: KeyboardEvent) => onKey.current(event);
    document.addEventListener("keydown", listener);
    return () => document.removeEventListener("keydown", listener);
  }, []);

  async function showOnTile(photoId: string) {
    setBusy(true);
    setMessage("");
    try {
      const response = await fetch(`/api/sent/mailings/${mailing.id}/cover`, {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ photoId }),
      });
      if (!response.ok) throw await errorFrom(response, "Could not change the tile picture.");
      onChanged((await response.json() as { mailing: Mailing }).mailing);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Could not change the tile picture.");
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    setBusy(true);
    setMessage("");
    try {
      await onRemove();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Could not remove that mailing.");
      setBusy(false);
    }
  }

  const photoButton = (photo: SentPhoto) => {
    const index = photos.findIndex((shown) => shown.photo.id === photo.id);
    if (index === -1) return <span key={photo.id} className="sent-not-kept">Not uploaded</span>;
    return (
      <button key={photo.id} type="button" className="sent-photo-button" aria-label={`Open ${photos[index].caption}`} onClick={() => go(index)}>
        <img src={`${photo.url}?w=320`} alt="" loading="lazy" />
      </button>
    );
  };

  const items = mailing.items.map((item, index) => {
    const card = mailing.items.slice(0, index + 1).filter((before) => before.kind === "postcard").length;
    const sides = item.kind === "envelope" || item.kind === "postcard";
    const name = item.kind === "envelope" ? "Envelope" : item.kind === "postcard" ? `Postcard ${card}`
      : item.kind === "letter" ? `Letter · ${item.photos.length} page${item.photos.length === 1 ? "" : "s"}` : "Extras";
    return (
      <li key={item.id} className="sent-item">
        <h3>{name}</h3>
        <div className="sent-item-photos">
          {sides
            ? (["front", "back"] as const).map((side) => {
              const photo = item.photos.find((p) => p.side === side);
              return photo ? photoButton(photo) : <span key={side} className="sent-not-kept">{side === "front" ? "Front" : "Back"} not kept</span>;
            })
            : [...item.photos].sort((a, b) => a.position - b.position).map(photoButton)}
        </div>
      </li>
    );
  });

  const how = [acquisitionLabels[mailing.acquisitionType], acquisitionDetailLabels[mailing.acquisitionDetail]].filter(Boolean).join(" · ");
  const coverCaption = photos.find(({ photo }) => photo.id === mailing.coverPhotoId)?.caption;

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => view !== "edit" && event.target === event.currentTarget && onClose()}>
      <section className="modal sent-sheet" role="dialog" aria-modal="true" aria-labelledby="sent-sheet-title">
        <div className="modal-header">
          <div>
            <p className="eyebrow">{view === "edit" ? "Changing this mailing" : reading ? reading.caption : "Mailing sent"}</p>
            <h2 id="sent-sheet-title">{title}</h2>
          </div>
          <button ref={closeButton} type="button" className="close-button" onClick={onClose} aria-label="Close">×</button>
        </div>

        {view === "edit" ? (
          <MailingForm
            mailing={mailing}
            mode="edit"
            knownThreads={knownThreads}
            knownCountries={knownCountries}
            onSaved={(updated) => { onChanged(updated); go("mailing"); }}
            onCancel={() => go("mailing")}
            onRemove={onRemove}
          />
        ) : reading && typeof view === "number" ? (
          <div className="sent-reader">
            <a href={reading.photo.url} target="_blank" rel="noreferrer" aria-label={`Open ${reading.caption} at full resolution`}>
              <img src={`${reading.photo.url}?w=1024`} alt={`${reading.caption}, to ${whoTo(mailing)}`} />
            </a>
            <div className="sent-reader-nav">
              <button type="button" className="secondary-button" disabled={view === 0} onClick={() => go(view - 1)}>← Before</button>
              <span className="sent-reader-count">{view + 1} of {photos.length}</span>
              <button type="button" className="secondary-button" disabled={view === photos.length - 1} onClick={() => go(view + 1)}>Next →</button>
            </div>
            <div className="sent-reader-actions">
              {reading.photo.id === mailing.coverPhotoId
                ? <span className="sent-on-tile">✓ This is on the tile</span>
                : <button type="button" className="primary-button" disabled={busy} onClick={() => showOnTile(reading.photo.id)}>Show this on the tile</button>}
              <button type="button" className="link-button" onClick={() => go("mailing")}>Back to the whole mailing</button>
            </div>
          </div>
        ) : (
          <div className="sent-detail">
            <p className="sent-tally">{mailingTally(mailing.items.map((item) => ({ kind: item.kind, pages: item.photos })))}</p>
            <dl className="sent-detail-fields">
              <dt>To</dt>
              <dd>{[mailing.recipientName, mailing.recipientUser && `@${mailing.recipientUser}`].filter(Boolean).join(" · ")}</dd>
              {whereTo(mailing) && <><dt>Where</dt><dd>{whereTo(mailing)}</dd></>}
              <dt>Sent</dt><dd>{mailing.sentDate ? formatDate(mailing.sentDate) : "No date yet"}</dd>
              {how && <><dt>How</dt><dd>{how}</dd></>}
              {threadLine(mailing) && <><dt>Thread</dt><dd>{threadLine(mailing)}</dd></>}
              {mailing.note && <><dt>Note</dt><dd className="sent-note">{mailing.note}</dd></>}
              {coverCaption && <><dt>Tile</dt><dd>{coverCaption}{!mailing.coverPicked && " (until you pick one)"}</dd></>}
            </dl>
            <ol className="sent-items">{items}</ol>
            <div className="sent-actions">
              <div className="sent-actions-main">
                <button type="button" className="secondary-button" onClick={() => go("edit")}>Edit</button>
              </div>
              {confirmDelete ? (
                <span className="sent-confirm">
                  Delete this mailing? Its photos stay in the nightly backup.
                  <button type="button" className="text-button is-danger" disabled={busy} onClick={remove}>Delete it</button>
                  <button type="button" className="text-button" onClick={() => setConfirmDelete(false)}>Keep it</button>
                </span>
              ) : (
                <button type="button" className="text-button is-quiet" onClick={() => setConfirmDelete(true)}>Delete this mailing</button>
              )}
            </div>
          </div>
        )}
        <p className="sent-message" aria-live="polite">{message}</p>
      </section>
    </div>
  );
}

/** One mailing to upload: its fields, and its scans in page order with what each is guessed to be. */
type UploadJob = {
  name: string;
  load: () => Promise<{ scans: Blob[]; roles: PageRole[]; fields: Partial<Fields> }>;
};

/**
 * The mailings Joey sends (#56, #63): a bulk upload of Notebloc PDFs and photos, a review
 * list to check each mailing and say what each page is, and a lookup for "what did I
 * send them?" before she writes again.
 *
 * Everything on this page is hers alone. Envelopes and backs show home addresses, which
 * she keeps so she can write to the same people again; the server only hands them to the
 * owner.
 */
export default function SentCardsPage() {
  const [viewer, setViewer] = useState<Viewer>("loading");
  const [notice, setNotice] = useState("");
  const [mailings, setMailings] = useState<Mailing[]>([]);
  const [received, setReceived] = useState<ReceivedCard[]>([]);

  const [pdfs, setPdfs] = useState<File[]>([]);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [singles, setSingles] = useState<ReadonlySet<string>>(new Set());
  const [swapped, setSwapped] = useState<ReadonlySet<string>>(new Set());
  const [picking, setPicking] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState("");
  const [failures, setFailures] = useState<string[]>([]);

  const [reviewId, setReviewId] = useState("");
  const [typed, setTyped] = useState("");
  const lookup = useRef<HTMLElement | null>(null);

  // The sent map links here with ?country= or ?region=, as the received map links to the
  // collection, and the same helpers read it so the map's count and this list agree.
  const [searchParams, setSearchParams] = useSearchParams();
  const countryParam = searchParams.get("country");
  const regionParam = searchParams.get("region");
  const placeKind = countryParam ? "country" : regionParam ? "region" : "";
  const placeValue = countryParam
    ? countryFromParam(countryParam)
    : regionParam ? stateFromParam(regionParam) : "";
  // A mailing with no country is on no map, so this is the only place she can find them.
  const missingCountry = searchParams.get("missing") === "country";

  function clearPlaceFilter() {
    const next = new URLSearchParams(searchParams);
    next.delete("country");
    next.delete("region");
    next.delete("missing");
    setSearchParams(next, { replace: true });
  }

  function showMissingCountry() {
    const next = new URLSearchParams();
    next.set("missing", "country");
    setSearchParams(next);
  }
  const [sheet, setSheet] = useState<{ id: string; view: SheetView } | null>(null);
  const sheetOpener = useRef<HTMLElement | null>(null);
  const photoUrls = useRef<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const me = await (await fetch("/api/me", { cache: "no-store" })).json() as { isOwner: boolean };
      if (!me.isOwner) {
        if (!cancelled) setViewer("other");
        return;
      }
      const [sentResponse, receivedResponse] = await Promise.all([
        fetch("/api/sent", { cache: "no-store" }),
        fetch("/api/postcards", { cache: "no-store" }),
      ]);
      if (!sentResponse.ok || !receivedResponse.ok) throw new Error("Could not load cards");
      const sentPayload = await sentResponse.json() as { mailings: Mailing[] };
      const receivedPayload = await receivedResponse.json() as { postcards: ReceivedCard[] };
      if (cancelled) return;
      setMailings(sentPayload.mailings);
      setReceived(receivedPayload.postcards);
      setViewer("owner");
    })().catch(() => {
      if (!cancelled) {
        setNotice("This page could not load your sent mailings.");
        setViewer("other");
      }
    });
    return () => { cancelled = true; };
  }, []);

  // The preview thumbnails hold the photos in memory; let them go when the page does.
  useEffect(() => () => photoUrls.current.forEach((url) => URL.revokeObjectURL(url)), []);

  const drafts = useMemo(() => mailings.filter((mailing) => mailing.isDraft), [mailings]);
  const saved = useMemo(() => mailings.filter((mailing) => !mailing.isDraft), [mailings]);
  const noCountry = useMemo(() => saved.filter((mailing) => !mailing.country.trim()), [saved]);
  const reviewing = drafts.find((mailing) => mailing.id === reviewId) ?? drafts[0];
  const pairs = useMemo(() => pairPhotos(photos, singles), [photos, singles]);
  const mailingsReady = pdfs.length + pairs.length;

  const knownThreads = useMemo(() => {
    const byKey = new Map<string, string>();
    for (const name of [...mailings.map((mailing) => mailing.thread), ...received.map((card) => card.ownerThread?.thread ?? "")]) {
      const key = threadKey(name);
      if (key && !byKey.has(key)) byKey.set(key, name.trim());
    }
    return [...byKey.values()].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  }, [mailings, received]);
  const knownCountries = useMemo(
    () => [...new Set([...mailings.map((mailing) => mailing.country), ...received.map((card) => card.originCountry)].filter(Boolean))]
      .sort((a, b) => a.localeCompare(b)),
    [mailings, received],
  );

  const results = useMemo(
    () => saved.filter((mailing) =>
      matchesSentCard(mailing, typed) && (!placeKind || sentToPlace(mailing, placeKind, placeValue)) &&
      (!missingCountry || !mailing.country.trim())),
    [saved, typed, placeKind, placeValue, missingCountry],
  );

  // Arriving from the map, the list is what she came for, not the upload above it.
  const arrivedFromMap = Boolean(placeKind) && viewer === "owner";
  useEffect(() => {
    if (arrivedFromMap) lookup.current?.scrollIntoView({ block: "start" });
  }, [arrivedFromMap]);
  const people = useMemo(() => new Set(results.map(personOf)), [results]);
  const onePerson = typed.trim() && people.size === 1 ? [...people][0] : "";
  // Everything sent to that one person, not only what the words matched.
  const toThem = useMemo(() => onePerson ? saved.filter((mailing) => personOf(mailing) === onePerson) : [], [saved, onePerson]);
  const fromThem = useMemo(
    () => onePerson && !onePerson.startsWith("name:") ? received.filter((card) => senderKey(card.senderUser) === onePerson) : [],
    [received, onePerson],
  );
  const fromThemStats = ratingStats(fromThem);

  async function pick(event: ChangeEvent<HTMLInputElement>) {
    const chosen = Array.from(event.target.files ?? []);
    event.target.value = "";
    if (!chosen.length) return;
    setPicking(true);
    setFailures([]);
    setProgress("");
    const skippedNames: string[] = [];
    const newPdfs: File[] = [];
    const newPhotos: Photo[] = [];
    const seen = new Set([...pdfs, ...photos.map((photo) => photo.file)].map((file) => `${file.name}:${file.size}`));
    for (const file of chosen) {
      if (seen.has(`${file.name}:${file.size}`)) continue;
      seen.add(`${file.name}:${file.size}`);
      if (isPdf(file)) {
        // A PDF is read when its turn comes to upload, not now: 325 of them held in memory
        // at once would be more than a phone can spare.
        newPdfs.push(file);
      } else if (file.type.startsWith("image/")) {
        try {
          // Photos are copied now, as on the received side: Android can refuse to hand over
          // a photo's bytes a few minutes after it was picked (chosen-photo.ts).
          const copy = await readChosenPhoto(file);
          const takenAt = readTakenAt(await copy.arrayBuffer()) ?? (file.lastModified || null);
          const url = URL.createObjectURL(copy);
          photoUrls.current.push(url);
          newPhotos.push({ id: `${file.name}:${file.size}:${file.lastModified}`, name: file.name, takenAt, file: copy, url });
        } catch (error) {
          skippedNames.push(`${file.name}: ${error instanceof Error ? error.message : "could not be read."}`);
        }
      } else {
        skippedNames.push(`${file.name}: not a PDF or a photo.`);
      }
    }
    setPdfs((current) => [...current, ...newPdfs]);
    setPhotos((current) => [...current, ...newPhotos]);
    setFailures(skippedNames);
    setPicking(false);
  }

  function clearPicked() {
    photoUrls.current.forEach((url) => URL.revokeObjectURL(url));
    photoUrls.current = [];
    setPdfs([]);
    setPhotos([]);
    setSingles(new Set());
    setSwapped(new Set());
  }

  function toggle(set: ReadonlySet<string>, id: string) {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  }

  async function reloadSent() {
    const response = await fetch("/api/sent", { cache: "no-store" });
    if (!response.ok) throw new Error("Could not load your sent mailings.");
    const payload = await response.json() as { mailings: Mailing[] };
    setMailings(payload.mailings);
  }

  /**
   * Sends one mailing: the draft first, with every page in place and named by its hash,
   * then each photo the server is still waiting for, one request at a time. A doc picked
   * again after its upload stopped halfway gets only its missing pages.
   */
  async function sendMailing(job: UploadJob, index: number, total: number) {
    const { scans, roles, fields } = await job.load();
    const hashes = await Promise.all(scans.map(async (scan) => sha256Hex(await scan.arrayBuffer())));
    const { items } = itemsFromRoles(skipRepeatedScans(roles, hashes));
    const response = await fetch("/api/sent/mailings", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        ...fields,
        sourceName: job.name,
        items: items.map((item) => ({ kind: item.kind, photos: item.pages.map((p) => ({ hash: hashes[p.page], side: p.side, position: p.page })) })),
      }),
    });
    if (!response.ok) throw await errorFrom(response, "could not be uploaded.");
    const { status, mailing } = await response.json() as { status: "added" | "resumed" | "skipped"; mailing: Mailing };
    for (const [done, photo] of mailing.pending.entries()) {
      const page = hashes.indexOf(photo.hash);
      if (page === -1) continue;
      setProgress(`Adding mailing ${index + 1} of ${total}, photo ${done + 1} of ${mailing.pending.length}…`);
      // Re-encoded on the phone, which drops everything hidden in the photo, the location
      // included. The server strips it again on the way to disk.
      const clean = await sanitizeImage(scans[page], `page-${page + 1}.jpg`);
      const form = new FormData();
      form.append("photo", clean, clean.name);
      const sent = await fetch(`/api/sent/photos/${photo.id}`, { method: "POST", body: form });
      if (!sent.ok) throw await errorFrom(sent, `page ${page + 1} could not be uploaded.`);
    }
    return status;
  }

  async function upload() {
    const jobs: UploadJob[] = [
      ...pdfs.map((file) => ({
        name: file.name,
        load: async () => {
          let bytes: Uint8Array;
          try {
            bytes = new Uint8Array(await file.arrayBuffer());
          } catch {
            throw new Error("could not be read from the phone. Choose it again.");
          }
          // Every page is kept (#63), and its shape goes into the guess at what it is.
          const scans = extractPdfScans(bytes);
          return {
            scans: scans.map((scan) => new Blob([scan], { type: "image/jpeg" })),
            roles: guessRoles(scans.map(jpegSize)),
            fields: parseDocTitle(file.name),
          };
        },
      })),
      ...pairs.map((pair) => {
        const [front, back] = swapped.has(pair.front.id) && pair.back ? [pair.back, pair.front] : [pair.front, pair.back];
        return {
          name: [front.name, back?.name].filter(Boolean).join(" + "),
          load: async () => ({
            scans: back ? [front.file, back.file] : [front.file],
            roles: (back ? ["front", "back"] : ["front"]) as PageRole[],
            fields: { sentDate: sentDateFrom(front.takenAt) },
          }),
        };
      }),
    ];

    setUploading(true);
    setFailures([]);
    let added = 0;
    let finished = 0;
    let skipped = 0;
    const failed: string[] = [];
    for (let index = 0; index < jobs.length; index += 1) {
      const job = jobs[index];
      setProgress(`Adding mailing ${index + 1} of ${jobs.length}…`);
      try {
        const status = await sendMailing(job, index, jobs.length);
        if (status === "added") added += 1;
        else if (status === "resumed") finished += 1;
        else skipped += 1;
      } catch (error) {
        const reason = error instanceof Error ? error.message : "could not be uploaded.";
        failed.push(`${job.name}: ${reason}`);
      }
    }
    clearPicked();
    try {
      await reloadSent();
    } catch {
      failed.push("The review list could not be refreshed. Reload the page to see the new mailings.");
    }
    const words = [`Added ${added} mailing${added === 1 ? "" : "s"} to your review list.`];
    if (finished) words.push(`Finished uploading ${finished} that had stopped partway.`);
    if (skipped) words.push(`${skipped} ${skipped === 1 ? "was" : "were"} already there.`);
    if (failed.length) words.push(`${failed.length} could not be added. Choose ${failed.length === 1 ? "it" : "them"} again to try once more.`);
    setProgress(words.join(" "));
    setFailures(failed);
    setUploading(false);
  }

  function replaceMailing(mailing: Mailing) {
    setMailings((current) => current.map((item) => item.id === mailing.id ? mailing : item));
  }

  /** The next mailing on the review list after this one, going back to the start at the end. */
  function nextDraftAfter(id: string) {
    const index = drafts.findIndex((mailing) => mailing.id === id);
    const rest = [...drafts.slice(index + 1), ...drafts.slice(0, index)];
    return rest[0]?.id ?? "";
  }

  async function remove(mailing: Mailing) {
    const response = await fetch(`/api/sent/mailings/${mailing.id}`, { method: "DELETE" });
    if (!response.ok) throw await errorFrom(response, "Could not remove that mailing.");
    const next = mailing.isDraft ? nextDraftAfter(mailing.id) : "";
    setMailings((current) => current.filter((item) => item.id !== mailing.id));
    if (mailing.isDraft) setReviewId(next);
    if (sheet?.id === mailing.id) closeSheet();
  }

  function openSheet(id: string, opener: HTMLElement) {
    sheetOpener.current = opener;
    setSheet({ id, view: "mailing" });
  }

  function closeSheet() {
    setSheet(null);
    sheetOpener.current?.focus();
  }

  const opened = sheet && saved.find((mailing) => mailing.id === sheet.id);

  const header = (
    <header className="topbar">
      <a className="brand" href="/" aria-label="My Collection of Connection home">
        <span className="brand-mark">✉</span>
        <span>My Collection of Connection</span>
      </a>
      <a className="owner-link" href="/">← Back to the collection</a>
    </header>
  );

  if (viewer !== "owner") {
    return (
      <main className="app-shell">
        {header}
        <section className="auth-shell">
          <div className="empty-state auth-card">
            <div className="empty-stamp">{viewer === "loading" ? "···" : "✉"}</div>
            <h1>{viewer === "loading" ? "Opening your sent cards…" : "Sent cards are private"}</h1>
            {viewer === "other" && <p>{notice || "Only the collection owner can see this page."}</p>}
            {viewer === "other" && !notice && <p className="auth-back"><a href="/signin">Owner sign in</a></p>}
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="app-shell ratings-page sent-page">
      {header}

      <section className="ratings-intro">
        <p className="eyebrow">Only you can see this page</p>
        <h1>Cards I've sent</h1>
        <p className="ratings-hint">
          {saved.length} saved{drafts.length > 0 && `, ${drafts.length} waiting to be checked`}. Visitors only
          see how many you've sent and where they went.
        </p>
      </section>

      <section className="ratings-section" aria-labelledby="sent-add-title">
        <div className="section-heading compact">
          <div><p className="eyebrow">Bringing mailings in</p><h2 id="sent-add-title">Add sent mailings</h2></div>
        </div>
        <p className="ratings-hint">
          Pick PDFs from Notebloc and photos together, as many as you like. A PDF is one mailing, and every page
          in it is kept: the review list guesses what each page is, and you fix anything it got wrong. Photos are
          paired in the order they were taken, front first, each pair a postcard.
        </p>
        <label className={`primary-button sent-pick${picking || uploading ? " is-busy" : ""}`}>
          {picking ? "Reading…" : "Choose PDFs or photos"}
          <input type="file" accept="application/pdf,.pdf,image/*" multiple hidden disabled={picking || uploading} onChange={pick} />
        </label>

        {mailingsReady > 0 && (
          <div className="sent-batch">
            <div className="sent-batch-head">
              <h3>{mailingsReady} mailing{mailingsReady === 1 ? "" : "s"} ready</h3>
              <button type="button" className="text-button is-quiet" disabled={uploading} onClick={clearPicked}>Clear</button>
            </div>
            <ul className="sent-batch-list">
              {pdfs.map((file) => {
                const details = parseDocTitle(file.name);
                const read = [threadLine(details),
                  details.recipientName, whereTo(details), details.recipientUser && `@${details.recipientUser}`].filter(Boolean).join(" · ");
                return (
                  <li key={`${file.name}:${file.size}`} className="sent-batch-item is-pdf">
                    <div className="sent-batch-meta">
                      <span className="sent-file"><span className="sent-kind">PDF</span>{file.name}</span>
                      <span className="sent-how">{read ? `Read from the title: ${read}` : "Nothing to read from the title"}</span>
                    </div>
                  </li>
                );
              })}
              {pairs.map((pair) => {
                const isSwapped = swapped.has(pair.front.id) && pair.back;
                const [front, back] = isSwapped ? [pair.back!, pair.front] : [pair.front, pair.back];
                return (
                  <li key={pair.front.id} className="sent-batch-item">
                    <div className="sent-thumbs">
                      <figure><img src={front.url} alt="" /><figcaption>Front</figcaption></figure>
                      {back ? <figure><img src={back.url} alt="" /><figcaption>Back</figcaption></figure> : <figure className="is-missing"><span>—</span><figcaption>No back</figcaption></figure>}
                    </div>
                    <div className="sent-batch-meta">
                      <span className="sent-file"><span className="sent-kind">Photos</span>{[front.name, back?.name].filter(Boolean).join(" + ")}</span>
                      <span className="sent-how">
                        {front.takenAt ? `Taken ${new Date(front.takenAt).toLocaleString()}` : "No time on the photo"}
                        {sentDateFrom(front.takenAt) && ` · sent date ${formatDate(sentDateFrom(front.takenAt))}`}
                      </span>
                      <span className="sent-batch-actions">
                        {pair.back && (
                          <button type="button" className="link-button" disabled={uploading} onClick={() => setSwapped((current) => toggle(current, pair.front.id))}>
                            Swap front and back
                          </button>
                        )}
                        {(pair.back || singles.has(pair.front.id)) && (
                          <button type="button" className="link-button is-quiet" disabled={uploading} onClick={() => setSingles((current) => toggle(current, pair.front.id))}>
                            {singles.has(pair.front.id) ? "Pair it with the next photo" : "This front has no back"}
                          </button>
                        )}
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
            <button type="button" className="primary-button" disabled={uploading} onClick={upload}>
              {uploading ? "Adding…" : `Add ${mailingsReady} mailing${mailingsReady === 1 ? "" : "s"} to the review list`}
            </button>
          </div>
        )}
        <p className="sent-progress" aria-live="polite">{progress}</p>
        {failures.length > 0 && <ul className="sent-failures">{failures.map((line) => <li key={line}>{line}</li>)}</ul>}
      </section>

      <section className="ratings-section" aria-labelledby="sent-review-title">
        <div className="section-heading compact">
          <div><p className="eyebrow">Checking each mailing</p><h2 id="sent-review-title">Review list</h2></div>
          {drafts.length > 0 && <p>{drafts.length} to check</p>}
        </div>
        {reviewing ? (
          <>
            <p className="ratings-hint">Green boxes were filled in from the file name or the photo. Saving takes the mailing off this list.</p>
            <MailingForm
              key={reviewing.id}
              mailing={reviewing}
              mode="review"
              knownThreads={knownThreads}
              knownCountries={knownCountries}
              onSaved={(mailing) => {
                const next = nextDraftAfter(mailing.id);
                replaceMailing(mailing);
                setReviewId(next);
              }}
              onSkip={() => setReviewId(nextDraftAfter(reviewing.id))}
              onRemove={() => remove(reviewing)}
            />
          </>
        ) : (
          <p className="quick-rate-empty">Nothing waiting. Mailings you add above show up here to check.</p>
        )}
      </section>

      <section className="ratings-section" aria-labelledby="sent-lookup-title" ref={lookup}>
        <div className="section-heading compact">
          <div><p className="eyebrow">Before you write again</p><h2 id="sent-lookup-title">What did I send them?</h2></div>
        </div>
        {placeKind && (
          <div className="place-chip">
            <span>Mailings to <strong>{titleCase(placeValue)}</strong></span>
            <button type="button" onClick={clearPlaceFilter} aria-label={`Stop showing only mailings to ${titleCase(placeValue)}`}>Show all ×</button>
            <a className="back-to-map" href="/maps?showing=sent">← Back to the maps</a>
          </div>
        )}
        {missingCountry && (
          <div className="place-chip">
            <span>Mailings with <strong>no country</strong></span>
            <button type="button" onClick={clearPlaceFilter} aria-label="Stop showing only mailings with no country">Show all ×</button>
          </div>
        )}
        {!missingCountry && noCountry.length > 0 && (
          <p className="sent-missing">
            <button type="button" className="text-button" onClick={showMissingCountry}>
              {noCountry.length === 1 ? "1 mailing is" : `${noCountry.length} mailings are`} missing a country →
            </button>
          </p>
        )}
        <label className="lookup-search sent-search">
          Username, name, thread, or place
          <input type="search" value={typed} onChange={(event) => setTyped(event.target.value)}
            placeholder="@username, name, group, or country" autoComplete="off" />
        </label>
        <div className="sent-summary" aria-live="polite">
          {typed.trim() ? (
            results.length ? (
              onePerson ? (
                <span>
                  Sent {whoTo(toThem[0])} <strong>{plural(toThem.length, "mailing", "mailings")}</strong>{" "}
                  ({plural(toThem.reduce((count, mailing) => count + mailing.items.filter((item) => item.kind === "postcard").length, 0), "postcard", "postcards")},{" "}
                  {plural(toThem.filter((mailing) => mailing.items.some((item) => item.kind === "letter")).length, "letter", "letters")})
                  {!onePerson.startsWith("name:") && (fromThem.length ? (
                    <span className="sent-from-them">
                      received {plural(fromThem.length, "card", "cards")} from them
                      {fromThemStats.average !== null && ` · ${formatAverage(fromThemStats.average)}`}
                    </span>
                  ) : <span className="muted"> · no cards from them yet</span>)}
                </span>
              ) : <span><strong>{plural(results.length, "mailing", "mailings")}</strong> sent to {people.size} people</span>
            ) : <span>You haven't saved a mailing for <strong>{typed.trim()}</strong>{placeKind && ` to ${titleCase(placeValue)}`} yet.</span>
          ) : missingCountry ? (
            results.length
              ? <span><strong>{plural(results.length, "mailing", "mailings")}</strong> with no country. Open one to add it.</span>
              : <span>Every mailing has a country now.</span>
          ) : placeKind ? (
            <span><strong>{plural(results.length, "mailing", "mailings")}</strong> sent to {titleCase(placeValue)}</span>
          ) : <span>{saved.length ? "Your most recent mailings. Type to search them all." : "Mailings you save show up here."}</span>}
        </div>
        <ul className="sent-tiles">
          {(typed.trim() || placeKind || missingCountry ? results : results.slice(0, 12)).map((mailing) => (
            <li key={mailing.id}>
              <MailingTile mailing={mailing} onOpen={(opener) => openSheet(mailing.id, opener)} />
            </li>
          ))}
        </ul>
      </section>

      {opened && sheet && (
        <MailingSheet
          key={opened.id}
          mailing={opened}
          view={sheet.view}
          onView={(view) => setSheet({ id: opened.id, view })}
          onClose={closeSheet}
          onChanged={replaceMailing}
          onRemove={() => remove(opened)}
          knownThreads={knownThreads}
          knownCountries={knownCountries}
        />
      )}
    </main>
  );
}
